import React, { useState, useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ChevronRight, ChevronDown, Circle, User, LogOut } from "lucide-react";
import { cn } from "../../lib/utils";
import { LayoutConfig } from "./types";
import { logout } from "wasp/client/auth";
import logo from "../../client/static/logo.webp";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { useViewMode } from "../../client/contexts/ViewModeContext";

interface CommonSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  config: LayoutConfig;
  onNavigate?: () => void;
}

export default function CommonSidebar({
  isOpen,
  onToggle,
  config,
  onNavigate,
}: CommonSidebarProps) {
  const location = useLocation();
  const params = useParams<{ slug?: string }>();
  const { selectedLocation } = useLocationContext();
  const { viewMode } = useViewMode();
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(
    config.menuSections.reduce(
      (acc, section) => {
        acc[section.label] = section.defaultOpen ?? true;
        return acc;
      },
      {} as Record<string, boolean>,
    ),
  );

  // When view mode (Community/Business) changes, expand all visible menu sections
  useEffect(() => {
    setExpandedSections((prev) => {
      const next = { ...prev };
      config.menuSections.forEach((section) => {
        next[section.label] = true;
      });
      return next;
    });
  }, [viewMode, config.menuSections]);

  const toggleSection = (sectionLabel: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionLabel]: !prev[sectionLabel],
    }));
  };

  // Helper function to get location-aware path
  const getLocationAwarePath = (basePath: string) => {
    // Marketplace (external) and Academics are location-independent
    const globalPaths = [
      "/retail",
      "/textbooks",
      "/services?category=TUTORING",
    ];

    if (globalPaths.some((p) => basePath.startsWith(p))) {
      return basePath;
    }

    // Use the slug from URL params or from selected location
    const locationSlug = params.slug || selectedLocation?.locationCode;
    // Don't prepend location for dashboard or admin routes
    if (
      locationSlug &&
      basePath !== "/dashboard" &&
      !basePath.startsWith("/admin")
    ) {
      return `/locations/${locationSlug}${basePath}`;
    }
    return basePath;
  };

  const renderMenuItem = (item: any, level = 0) => {
    const Icon = item.icon;
    const locationAwarePath = getLocationAwarePath(item.path);
    // Check if active by matching both the base path and location-aware path
    const isActive =
      location.pathname === item.path ||
      location.pathname === locationAwarePath ||
      location.pathname.endsWith(item.path);
    const hasChildren = item.children && item.children.length > 0;

    if (hasChildren) {
      return (
        <div key={item.path} className="mb-1">
          <button
            onClick={() => toggleSection(item.label)}
            className="w-full flex items-center gap-2 px-4 py-2 text-sidebar-text-muted hover:bg-sidebar-hover transition-colors group"
          >
            {expandedSections[item.label] ? (
              <Circle className="w-4 h-4 text-sidebar-text-muted" />
            ) : (
              <Circle className="w-4 h-4 text-sidebar-text-muted" />
            )}
            <Icon
              className={cn("w-5 h-5", item.color || "text-sidebar-text-muted")}
            />
            <span className="flex-1 text-left text-sm font-medium">
              {item.label}
            </span>
            {expandedSections[item.label] ? (
              <ChevronDown className="w-4 h-4 text-sidebar-text-muted" />
            ) : (
              <ChevronRight className="w-4 h-4 text-sidebar-text-muted" />
            )}
          </button>

          {expandedSections[item.label] && (
            <div className="mt-1 ml-6">
              {item.children.map((child: any) =>
                renderMenuItem(child, level + 1),
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.path}
        to={locationAwarePath}
        onClick={onNavigate}
        className={cn(
          "w-full flex items-center gap-3 px-4 py-3 transition-colors group",
          isActive
            ? "bg-sidebar-active text-sidebar-text"
            : "text-sidebar-text hover:bg-sidebar-hover",
        )}
      >
        <Icon
          className={cn("w-5 h-5", item.color || "text-sidebar-text-muted")}
        />
        <span className="flex-1 text-left text-base font-medium">
          {item.label}
        </span>
      </Link>
    );
  };

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 h-full z-[60] transition-all duration-300 bg-sidebar-bg border-r border-sidebar-border flex flex-col overflow-hidden",
        isOpen ? "w-[var(--sidebar-width)]" : "w-0",
        "lg:translate-x-0",
        isOpen ? "translate-x-0" : "-translate-x-full",
      )}
    >
      {/* Brand Header */}
      <div
        className="border-b border-sidebar-border"
        style={{
          height: "var(--header-height)",
          padding: "0.75rem 1rem",
          minHeight: "3.5rem",
          maxHeight: "5rem",
        }}
      >
        <div className="flex items-center justify-between">
          <Link
            to={getLocationAwarePath("/dashboard")}
            onClick={onNavigate}
            className="flex flex-col select-none"
          >
            <div className="text-[10px] font-bold text-sidebar-text-muted uppercase tracking-[0.1em] leading-none mb-0.5">
              University of
            </div>
            <div className="text-xl font-extrabold text-sidebar-text leading-none tracking-tight">
              Ottawa
            </div>
          </Link>
          <button
            onClick={onToggle}
            className="p-2 hover:bg-sidebar-hover rounded-lg transition-colors"
            title="Collapse sidebar"
          >
            <ChevronRight className="w-4 h-4 text-sidebar-text-muted rotate-180" />
          </button>
        </div>
      </div>

      {/* Custom Sidebar Content */}
      {config.customSidebarContent && (
        <div className="border-b border-sidebar-border">
          {config.customSidebarContent}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto">
        {config.menuSections.map((section, index) => (
          <div
            key={section.label}
            className={
              index === 0
                ? "-mt-px"
                : index === config.menuSections.length - 1
                  ? ""
                  : "mb-0"
            }
          >
            {/* Section Header - Only show if label is not empty */}
            {section.label && (
              <button
                onClick={() => toggleSection(section.label)}
                className="w-full flex items-center gap-2 px-4 py-2 text-sidebar-text-muted hover:bg-sidebar-hover transition-colors group border-t sidebar-section-header border-sidebar-border"
              >
                <span className="flex-1 text-left text-base font-medium">
                  {section.label}
                </span>
              </button>
            )}

            {/* Section Items */}
            {(!section.label || expandedSections[section.label]) && (
              <div className={section.label ? "mt-1" : ""}>
                {section.items.map((item) => renderMenuItem(item))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Admin Dashboard Link - Only show on /admin page */}
      {location.pathname.startsWith("/admin") && (
        <div className="border-t border-sidebar-border p-2">
          <Link
            to="/dashboard"
            onClick={onNavigate}
            className="w-full flex items-center gap-3 px-4 py-2 text-sidebar-text hover:bg-sidebar-hover rounded-lg transition-colors"
          >
            <span className="text-base">Dashboard</span>
          </Link>
        </div>
      )}

      {/* Bottom Actions */}
      <div className="border-t border-sidebar-border p-2">
        <button
          onClick={() => logout()}
          className="w-full flex items-center gap-3 px-4 py-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span className="text-base">Logout</span>
        </button>
      </div>
    </aside>
  );
}
